"use client";

import { useEffect, useRef, useState } from "react";
import { BsRecordFill } from "react-icons/bs";

import { usePlayBackStore } from "~/stores/playBackStore";
import { useUploadThing } from "~/utils/uploadthing";

export default function Recorder({
  close,
  id,
}: {
  close: () => void;
  id: number | null;
}) {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [leftTimer, setLeftTimer] = useState(0);
  const [rightTimer, setRightTimer] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | undefined>(undefined);

  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const [files, setFiles] = useState<File[]>([]);

  const setIsLoading = usePlayBackStore((state) => state.setLoadingState);
  const setPlayBackURL = usePlayBackStore((state) => state.setAudioURL);

  const handleRecord = () => {
    setAudioUrl(undefined);
    setRightTimer(0);
    setProgress(0);
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          mediaRecorderRef.current = new MediaRecorder(stream);
          mediaRecorderRef.current.start();
          audioChunksRef.current = [];

          mediaRecorderRef.current.addEventListener(
            "dataavailable",
            (event: BlobEvent) => {
              audioChunksRef.current.push(event.data);
            },
          );

          setIsRecording(true);
        })
        .catch((err) => {
          console.error("Error accessing the microphone:", err);
        });
    }
  };

  const handleStop = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());

      mediaRecorderRef.current.addEventListener("stop", () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/mp3",
        });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audioFile = new File([audioBlob], `audio-${Date.now()}.mp3`, {
          type: "audio/mp3",
        });

        setAudioUrl(audioUrl);
        setFiles([audioFile]);
        setIsRecording(false);
      });
    }
  };

  const { startUpload, isUploading } = useUploadThing("audioUploader");

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (isRecording) {
      timer = setInterval(() => {
        setRightTimer((prevTime) => prevTime + 1);
      }, 1000);
    } else if (!isRecording && rightTimer !== 0 && timer) {
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [isRecording, rightTimer]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (isPlaying) {
      timer = setInterval(() => {
        setLeftTimer((prevTime) => prevTime + 1);
      }, 1000);
    } else if (!isPlaying && leftTimer !== 0 && timer) {
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [isPlaying, leftTimer]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  // This effect sets up and tears down the event listener for the audio element
  useEffect(() => {
    const audioElement = audioRef.current;

    const handleTimeUpdate = () => {
      if (audioElement) {
        const currentProgress =
          (audioElement.currentTime / audioElement.duration) * 100;
        setProgress(currentProgress);
      }
    };

    const handleAudioEnd = () => {
      setIsPlaying(false);
      setProgress(0);
      setLeftTimer(0);
      if (audioElement) {
        audioElement.currentTime = 0;
      }
    };

    if (audioElement) {
      audioElement.addEventListener("timeupdate", handleTimeUpdate);
      audioElement.addEventListener("ended", handleAudioEnd);
    }

    return () => {
      if (audioElement) {
        audioElement.removeEventListener("timeupdate", handleTimeUpdate);
        audioElement.removeEventListener("ended", handleAudioEnd);
      }
    };
  }, []);

  const togglePlayPause = () => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    if (isPlaying) {
      audioElement.pause();
    } else {
      void audioElement.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <>
      <audio ref={audioRef} src={audioUrl}>
        Your browser does not support the audio element.
      </audio>
      <div
        className={`mx-auto mt-16 max-w-fit flex-col items-center justify-center rounded-full border border-gray-300 bg-gray-50 p-6 shadow ${isUploading ? "animate-pulse" : ""}`}
      >
        <div className="flex items-center justify-center gap-10 rounded-md ">
          <button
            className={
              isRecording
                ? "animate-pulse text-red-600 hover:scale-110"
                : "text-red-600 hover:scale-110"
            }
            onClick={isRecording ? handleStop : handleRecord}
          >
            <BsRecordFill className="size-6" />
          </button>

          <button
            className={isRecording ? "hover:scale-110" : "opacity-35"}
            onClick={handleStop}
            disabled={!isRecording}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="size-6"
            >
              <path
                fillRule="evenodd"
                d="M4.5 7.5a3 3 0 0 1 3-3h9a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3h-9a3 3 0 0 1-3-3v-9Z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          <button
            className={audioUrl ? "hover:scale-110" : "opacity-35"}
            disabled={!audioUrl}
            onClick={togglePlayPause}
          >
            {isPlaying ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 5.25v13.5m-7.5-13.5v13.5"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="size-6"
              >
                <path
                  fillRule="evenodd"
                  d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>

          <button
            className={audioUrl ? "hover:scale-110" : "opacity-35"}
            disabled={!audioUrl}
            onClick={() => {
              setAudioUrl(undefined);
              setLeftTimer(0);
              setRightTimer(0);
              setProgress(0);
              setIsPlaying(false);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="size-5"
            >
              <path
                fillRule="evenodd"
                d="M16.5 4.478v.227a48.816 48.816 0 0 1 3.878.512.75.75 0 1 1-.256 1.478l-.209-.035-1.005 13.07a3 3 0 0 1-2.991 2.77H8.084a3 3 0 0 1-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 0 1-.256-1.478A48.567 48.567 0 0 1 7.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 0 1 3.369 0c1.603.051 2.815 1.387 2.815 2.951Zm-6.136-1.452a51.196 51.196 0 0 1 3.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 0 0-6 0v-.113c0-.794.609-1.428 1.364-1.452Zm-.355 5.945a.75.75 0 1 0-1.5.058l.347 9a.75.75 0 1 0 1.499-.058l-.346-9Zm5.48.058a.75.75 0 1 0-1.498-.058l-.347 9a.75.75 0 0 0 1.5.058l.345-9Z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <button
            className={audioUrl ? "hover:scale-110" : "opacity-35"}
            disabled={!audioUrl}
            onClick={async () => {
              setIsLoading(id!, true);
              close();
              await startUpload(files, { lineId: id! });
              if (audioUrl) {
                setPlayBackURL(id!, audioUrl);
              }
              setIsLoading(id!, false);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="size-6"
            >
              <path d="M11.47 1.72a.75.75 0 0 1 1.06 0l3 3a.75.75 0 0 1-1.06 1.06l-1.72-1.72V7.5h-1.5V4.06L9.53 5.78a.75.75 0 0 1-1.06-1.06l3-3ZM11.25 7.5V15a.75.75 0 0 0 1.5 0V7.5h3.75a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3h-9a3 3 0 0 1-3-3v-9a3 3 0 0 1 3-3h3.75Z" />
            </svg>
          </button>
        </div>
        <div className="mt-3 flex items-center gap-1">
          {formatTime(leftTimer)}
          <input
            type="range"
            min="0"
            max="100"
            value={progress}
            onChange={(e) => {
              const audioElement = audioRef.current;

              if (audioElement) {
                const newTime =
                  (audioElement.duration / 100) * parseInt(e.target.value);
                audioElement.currentTime = newTime;
                setProgress(parseInt(e.target.value));
              }
            }}
            disabled={!audioUrl}
            className="rounded-lg bg-gray-200 accent-rose-400"
          />
          {formatTime(rightTimer)}
        </div>
      </div>
    </>
  );
}
